/**
 * Retailer URL parser — extracts retailer name and ASIN/product info from shared URLs.
 * Accepts any valid URL; known retailers get product ID extraction.
 */

export interface ParsedRetailerUrl {
  retailer: string;
  retailerLabel: string;
  asin: string | null;
  isKnownRetailer: boolean;
  originalUrl: string;
}

interface RetailerPattern {
  slug: string;
  label: string;
  /** Host substrings to match (supports regional TLDs like amazon.co.uk) */
  hostMatches: string[];
  /** Regex applied to the URL pathname to extract the ASIN/product ID (first capture group) */
  pathPattern: RegExp;
}

const RETAILER_PATTERNS: RetailerPattern[] = [
  {
    slug: 'amazon',
    label: 'Amazon',
    hostMatches: ['amazon.com', 'amazon.co.uk', 'amazon.ca', 'amazon.de', 'amazon.fr', 'amazon.in', 'amzn.to'],
    // Matches /dp/ASIN, /gp/product/ASIN, or /gp/aw/d/ASIN
    pathPattern: /\/(?:dp|gp\/product|gp\/aw\/d)\/([A-Z0-9]{10})/i,
  },
  {
    slug: 'walmart',
    label: 'Walmart',
    hostMatches: ['walmart.com'],
    // Matches /ip/product-name/ITEMID or /ip/ITEMID
    pathPattern: /\/ip\/(?:[^/]+\/)?(\d+)/,
  },
  {
    slug: 'target',
    label: 'Target',
    hostMatches: ['target.com'],
    // Matches /p/product-name/-/A-PRODUCTID
    pathPattern: /\/p\/[^/]+\/-\/A-(\d+)/,
  },
];

/**
 * Derive a slug from a hostname, e.g. "www.bestbuy.com" → "bestbuy"
 */
function domainSlug(hostname: string): string {
  return hostname.replace(/^www\./, '').split('.')[0];
}

/**
 * Derive a display label from a hostname, e.g. "www.bestbuy.com" → "Bestbuy"
 */
function domainLabel(hostname: string): string {
  const slug = domainSlug(hostname);
  return slug.charAt(0).toUpperCase() + slug.slice(1);
}

/**
 * Parse a URL shared from any retailer or website.
 * Returns parsed info for any valid URL. Returns `null` only for non-URLs.
 */
export function parseRetailerUrl(rawUrl: string): ParsedRetailerUrl | null {
  let url: URL;
  try {
    url = new URL(rawUrl.trim());
  } catch {
    return null;
  }

  if (!url.protocol.startsWith('http')) return null;

  // Check known retailers first
  for (const pattern of RETAILER_PATTERNS) {
    const hostMatch = pattern.hostMatches.some(h => url.hostname.includes(h));
    if (!hostMatch) continue;

    const pathMatch = url.pathname.match(pattern.pathPattern);
    const asin = pattern.slug === 'amazon' ? (pathMatch?.[1] ?? null) : null;

    return {
      retailer: pattern.slug,
      retailerLabel: pattern.label,
      asin,
      isKnownRetailer: true,
      originalUrl: rawUrl.trim(),
    };
  }

  // Fallback: accept any valid http(s) URL
  return {
    retailer: domainSlug(url.hostname),
    retailerLabel: domainLabel(url.hostname),
    asin: null,
    isKnownRetailer: false,
    originalUrl: rawUrl.trim(),
  };
}

/** Check if a string looks like it contains a URL (useful for filtering share sheet text) */
export function extractUrlFromText(text: string): string | null {
  const urlMatch = text.match(/https?:\/\/[^\s]+/i);
  return urlMatch ? urlMatch[0] : null;
}
